import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Select,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { VersionCompareRow } from "./VersionList";

export type VersionComparePanelProps<T extends VersionCompareRow> = {
  versions: readonly T[];
  getOptionLabel: (v: T) => string;
  onShowDiff: (base: T, compare: T) => void;
  onSelectionChange?: (base: T | null, compare: T | null) => void;
  hideCompareAction?: boolean;
};

export function VersionComparePanel<T extends VersionCompareRow>({
  versions,
  getOptionLabel,
  onShowDiff,
  onSelectionChange,
  hideCompareAction = false,
}: VersionComparePanelProps<T>) {
  const sorted = useMemo(
    () => [...versions].sort((a, b) => a.sortKey - b.sortKey),
    [versions]
  );

  const [baseId, setBaseId] = useState<string>("");
  const [compareId, setCompareId] = useState<string>("");

  useEffect(() => {
    if (sorted.length === 0) {
      setBaseId("");
      setCompareId("");
      return;
    }

    const ids = new Set(sorted.map((v) => v.id));
    const newestId = sorted[sorted.length - 1].id;
    const previousId =
      sorted.length >= 2 ? sorted[sorted.length - 2].id : newestId;

    setBaseId((prev) => {
      if (!prev || !ids.has(prev)) return previousId;
      return prev;
    });

    setCompareId((prev) => {
      if (!prev || !ids.has(prev)) return newestId;
      return prev;
    });
  }, [sorted]);

  const base = sorted.find((v) => v.id === baseId) ?? null;
  const compare = sorted.find((v) => v.id === compareId) ?? null;
  const canCompare = Boolean(
    base && compare && base.id !== compare.id && sorted.length >= 2
  );
  const previousSelectionKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onSelectionChange) return;

    const selectionKey = `${base?.id ?? "nil"}::${compare?.id ?? "nil"}`;
    if (previousSelectionKeyRef.current === selectionKey) return;

    previousSelectionKeyRef.current = selectionKey;
    onSelectionChange(base, compare);
  }, [base, compare, onSelectionChange]);

  const handleBaseChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setBaseId(e.target.value);
  }, []);

  const handleCompareChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setCompareId(e.target.value);
    },
    []
  );

  const handleShow = useCallback(() => {
    if (!base || !compare || base.id === compare.id) return;
    onShowDiff(base, compare);
  }, [base, compare, onShowDiff]);

  if (sorted.length < 2) {
    return (
      <Box mt="16px" w="full" maxW={{ base: "100%", md: "720px" }}>
        <Text fontSize="sm" color="gray.600">
          2件以上のバージョンがあれば、任意の2件同士の差分を比較できます。
        </Text>
      </Box>
    );
  }

  return (
    <Box mt="16px" w="full" maxW={{ base: "100%", md: "720px" }}>
      <VStack align="stretch" spacing={4}>
        <Text fontSize="sm" color="gray.700">
          比較元（左）と比較先（右）を選び、差分を表示します。
        </Text>
        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={4}
          w="full"
          alignItems="start"
        >
          <FormControl>
            <FormLabel fontSize="sm">比較元（左）</FormLabel>
            <Select size="sm" value={baseId} onChange={handleBaseChange}>
              {sorted.map((v) => (
                <option key={v.id} value={v.id}>
                  {getOptionLabel(v)}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm">比較先（右）</FormLabel>
            <Select size="sm" value={compareId} onChange={handleCompareChange}>
              {sorted.map((v) => (
                <option key={v.id} value={v.id}>
                  {getOptionLabel(v)}
                </option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>
        {!hideCompareAction && (
          <Box>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={handleShow}
              isDisabled={!canCompare}
            >
              差分を表示
            </Button>
            {!canCompare && baseId === compareId && (
              <Text as="span" ml={3} fontSize="xs" color="gray.500">
                異なる2つを選んでください。
              </Text>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
}
